import {
  addDays,
  adjustFromTimeZone,
  FlaggedMeterEntity,
  MeterEntity,
  subDays,
  TemporalMeterFlowVolumeTotalEntry,
} from "@ots-frontend/ots-data";
import {useCallback, useState} from "react";
import {DataTable} from "../DataTable/DataTable";
import {ActionIcon, createStyles, Group} from "@mantine/core";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {useTranslation} from "react-i18next";
import {IconChevronRight, IconEye} from "@tabler/icons-react";
import {MeterCell} from "../cells/MeterCell/MeterCell";
import {usePagination} from "../DataTable/usePagination"
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useNavigate} from "react-router-dom";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const useStyles = createStyles((theme) => ({
  expandIcon: {
    transition: 'transform 0.2s ease',
  },
  expandIconRotated: {
    transform: 'rotate(90deg)',
  },
}));

const EMPTY_ARRAY: FlaggedMeterEntity[] = []

export type FlaggedMeterDataTableProps = EntityDataTableProps<FlaggedMeterEntity>

export const FlaggedMeterDataTable = (props: FlaggedMeterDataTableProps) => {
  const {records: flaggedMeters = EMPTY_ARRAY, recordsPerPageOptions, ...rest} = props;

  const {cx, classes} = useStyles();

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const pagination = usePagination({
    data: flaggedMeters,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const onReviewClick = useCallback((meter: MeterEntity, entry: TemporalMeterFlowVolumeTotalEntry) => {
    let fromDttm: number | undefined = undefined
    if (entry.fromDttm) {
      fromDttm = subDays(adjustFromTimeZone(entry.fromDttm), 1).getTime()
    }

    let toDttm: number | undefined = undefined
    if (entry.toDttm) {
      toDttm = addDays(adjustFromTimeZone(entry.toDttm), 1).getTime()
    }

    navigate("/pipelines/meter-flows/review", {
      state: {
        siteId: meter.objectId,
        fromDttm: fromDttm,
        toDttm: toDttm,
      }
    })
  }, [navigate])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      idAccessor={(record) => record.meter.id}
      rowExpansion={{
        allowMultiple: true,
        expanded: {
          recordIds: expandedIds,
          onRecordIdsChange: setExpandedIds
        },
        content: ({record}) => (
          <DataTable
            noHeader
            records={flaggedMeters.find((flagged) => flagged.meter.id === record.meter.id)?.flowVolumeFlags ?? []}
            columns={[
              {
                accessor: "meter.name",
                width: 100,
                render: ({fromDttm}) => {
                  return (
                    <Group spacing="xs" ml={30}>
                      <FormattedDateTime date={fromDttm}/>
                    </Group>
                  )
                }
              },
              {
                accessor: 'quantity',
                width: 100,
                render: ({quantity, unitOfMeasure}) => {
                  return (
                    <FormattedQuantity
                      value={{
                        number: quantity ?? 0,
                        units: unitOfMeasure
                      }}
                    />
                  )
                }
              },
              {
                accessor: "percentChange",
                width: 100,
                render: ({percentChange}) => {
                  if (percentChange) {
                    return (
                      <FormattedQuantity
                        value={percentChange}
                        options={{
                          style: "percent",
                          signDisplay: "always",
                        }}
                      />
                    )
                  }

                  return "-"
                }
              },
              {
                accessor: "actions",
                width: 20,
                render: (entry) => (
                  <Group spacing={4} position="right" noWrap>
                    <ActionIcon color="primary" onClick={() => onReviewClick(record.meter, entry)}>
                      <IconEye size={16}/>
                    </ActionIcon>
                  </Group>
                )
              }
            ]}
          />
        )
      }}
      columns={[
        {
          title: t("common.entity.meter.table.column.name", "Name"),
          accessor: "meter.name",
          width: 100,
          render: ({meter, flowVolumeFlags}) => (
            <Group spacing="xs">
              <IconChevronRight
                size="0.9em"
                className={cx(classes.expandIcon, {
                  [classes.expandIconRotated]: expandedIds.includes(meter.id),
                })}
              />
              <MeterCell
                meterId={meter.id}
                meterName={meter.name}
                type={meter.type}
                flowMeterType={meter.flowMeterType}
              />
              <FormattedQuantity
                value={flowVolumeFlags?.length ?? 0}
                options={{
                  maximumFractionDigits: 0
                }}/>
            </Group>
          ),
        },
        {
          title: 'Quantity',
          accessor: "quantity",
          width: 100,
        },
        {
          title: 'Percent Change',
          accessor: "percentChange",
          width: 100,
        },
        {
          title: '',
          accessor: "actions",
          width: 20,
        }
      ]}
    />
  );
}
